import { MarkupBasis } from '../types/member-resource'

/**
 * Cost-plus markup helpers.
 *
 * The canonical stored value is always a percent (`markupPercent`); a
 * multiplier is just another notation for the same number (3x = 200%).
 * These helpers convert between the two and format for display.
 */

export const MULTIPLIER_SYMBOL = '×'

export function percentToMultiplier(markupPercent: number): number {
  return 1 + markupPercent / 100
}

export function multiplierToPercent(multiplier: number): number {
  return (multiplier - 1) * 100
}

/** Trim to at most `digits` decimals and drop trailing zeros ("3", "1.25"). */
function trimDecimals(value: number, digits: number): string {
  return Number(value.toFixed(digits)).toString()
}

/** "3×" or "200%" for the raw value, without the trailing word. */
export function formatMarkupValue(
  markupPercent: number | string | null | undefined,
  basis: MarkupBasis | string | null | undefined,
): string | null {
  if (markupPercent == null || markupPercent === '') return null
  const pct = Number(markupPercent)
  if (!Number.isFinite(pct)) return null
  if (basis === MarkupBasis.MULTIPLIER) {
    return `${trimDecimals(percentToMultiplier(pct), 4)}${MULTIPLIER_SYMBOL}`
  }
  return `${trimDecimals(pct, 2)}%`
}

/** "3× markup" or "200% markup". Null when there is no markup to show. */
export function formatMarkup(
  markupPercent: number | string | null | undefined,
  basis: MarkupBasis | string | null | undefined,
): string | null {
  const value = formatMarkupValue(markupPercent, basis)
  return value == null ? null : `${value} markup`
}

export function describeMarkupFormula(
  basis: MarkupBasis | string | null | undefined,
): string {
  return basis === MarkupBasis.MULTIPLIER
    ? `Bill rate = Cost rate ${MULTIPLIER_SYMBOL} multiplier`
    : `Bill rate = Cost rate ${MULTIPLIER_SYMBOL} (1 + markup%)`
}
